// Public SMS consent / disclosure page. Linked from Twilio Toll-Free
// Verification (TFV) submission so TCR reviewers can see the explicit
// opt-in language. Also linked from the privacy policy and the onboarding
// admin-phone step.
//
// Content here exists for one purpose: satisfy carrier compliance review.
// Don't add login gates, marketing, or anything that obscures the disclosure
// — TCR auto-rejects pages that hide consent text behind auth or modals.

export function SmsConsent() {
  return (
    <main className="min-h-screen bg-stone-50 px-6 py-12">
      <div className="mx-auto max-w-2xl bg-white rounded-xl border border-stone-200 p-8 shadow-sm">
        <h1 className="text-2xl font-semibold tracking-tight">SMS Terms &amp; Consent</h1>
        <p className="mt-3 text-sm text-stone-500">
          Last updated: 2026-05-04 • Operator: Tall Poppy Group, LLC
        </p>

        <section className="mt-6 space-y-4 text-stone-800 leading-relaxed">
          <p>
            <strong>Inkling</strong> is a friction-free family calendar service operated by{" "}
            <strong>Tall Poppy Group, LLC</strong> (3 Via Oneg, Lafayette, CA 94549). When you
            sign up for Inkling and provide your phone number, you agree to receive text
            messages (SMS/MMS) from us at the number you provide.
          </p>

          <h2 className="text-lg font-medium pt-4">What you'll receive</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>One-time invitation messages when a family administrator adds you to their family calendar.</li>
            <li>Confirmation messages when you text an event into the family number (e.g. "Soccer practice Friday at 4pm").</li>
            <li>Clarification questions when an event you texted in is ambiguous.</li>
            <li>Brief acknowledgments to inbound messages so you know the system received them.</li>
          </ul>

          <h2 className="text-lg font-medium pt-4">How you opt in</h2>
          <p>
            Opt-in is always explicit. You become opted in by one of two paths:
          </p>
          <ol className="list-decimal pl-6 space-y-1">
            <li>
              <strong>Self-serve admin signup.</strong> You create a family at{" "}
              <a className="text-stone-900 underline" href="https://inkling.family">https://inkling.family</a>{" "}
              and provide your phone number on the onboarding form. Submitting that form is your
              consent to receive SMS confirmations and clarifications about events you text in.
            </li>
            <li>
              <strong>Family-member invitation.</strong> An existing family administrator adds you
              by phone number. You receive one invitation SMS asking you to reply <strong>YES</strong>{" "}
              to confirm. Replying YES is your consent; until you reply YES, no further messages are
              sent to your number.
            </li>
          </ol>

          <h2 className="text-lg font-medium pt-4">How you opt out</h2>
          <p>
            Reply <strong>STOP</strong>, <strong>UNSUBSCRIBE</strong>, <strong>CANCEL</strong>,{" "}
            <strong>END</strong>, or <strong>QUIT</strong> to any Inkling SMS at any time. We will
            stop sending SMS to that number immediately and confirm with one final unsubscribe
            message. You can re-subscribe by replying <strong>START</strong>.
          </p>
          <p>
            For help, reply <strong>HELP</strong> or <strong>INFO</strong> to any Inkling SMS, or
            email{" "}
            <a className="text-stone-900 underline" href="mailto:hello@inkling.family">
              hello@inkling.family
            </a>
            .
          </p>

          <h2 className="text-lg font-medium pt-4">Costs</h2>
          <p>
            Message and data rates may apply per your mobile carrier's plan. Inkling itself does
            not charge for the SMS you receive; your carrier may charge for SMS delivery depending
            on your plan.
          </p>

          <h2 className="text-lg font-medium pt-4">Frequency</h2>
          <p>
            Message frequency varies by your family's usage. Typical: 1-10 messages per day per
            family during active scheduling weeks; 0-2 messages per day otherwise.
          </p>

          <h2 className="text-lg font-medium pt-4">Privacy</h2>
          <p>
            Your phone number is used only for delivering the SMS messages described above and is
            not sold or shared with third parties for marketing. We share your number only with
            our SMS delivery providers (Twilio, Sendblue) for the purpose of sending and receiving
            the messages above. See our{" "}
            <a className="text-stone-900 underline" href="/privacy">privacy policy</a> for full
            data-handling details.
          </p>

          <h2 className="text-lg font-medium pt-4">Contact</h2>
          <p>
            Tall Poppy Group, LLC
            <br />
            3 Via Oneg
            <br />
            Lafayette, CA 94549
            <br />
            <a className="text-stone-900 underline" href="mailto:hello@inkling.family">
              hello@inkling.family
            </a>
          </p>
        </section>
      </div>
    </main>
  );
}
